// [claude-code 2026-04-03] Spring-physics enter/exit transitions (scale+bounce enter, decisive exit)
// [claude-code 2026-04-01] iOS26 Liquid Glass restyle + position-aware animation direction
import { useEffect, useState } from "react";
import {
  X,
  Check,
  AlertTriangle,
  Loader2,
  Info,
  BellOff,
  Activity,
} from "lucide-react";
import {
  useToast,
  type Toast,
  type ToastVariant,
} from "../../contexts/ToastContext";

/* ------------------------------------------------------------------ */
/*  Variant config — Zen: all gold-tinted, subtle icon differentiation */
/* ------------------------------------------------------------------ */

const VARIANT_CONFIG: Record<
  ToastVariant,
  { Icon: typeof Check; label?: string }
> = {
  success: { Icon: Check, label: "OK" },
  error: { Icon: AlertTriangle, label: "ALERT" },
  updating: { Icon: Loader2 },
  info: { Icon: Info },
  reminder: { Icon: AlertTriangle, label: "REMINDER" },
  vix: { Icon: Activity, label: "VIX" },
  watch: { Icon: AlertTriangle, label: "WATCH" },
};

/* ------------------------------------------------------------------ */
/*  Single toast item                                                  */
/* ------------------------------------------------------------------ */

function ToastItem({
  toast,
  onDismiss,
  onBlock,
}: {
  toast: Toast;
  onDismiss: (id: string) => void;
  onBlock: (toast: Toast) => void;
}) {
  const [entered, setEntered] = useState(false);
  const cfg = VARIANT_CONFIG[toast.variant];
  const isTopRight = toast.position === "top-right";
  const isBottomRight = toast.position === "bottom-right";

  useEffect(() => {
    const raf = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  const isVisible = entered && !toast.exiting;
  const hasDND = !!toast.notificationType;
  const slideX = isTopRight || isBottomRight ? "16px" : "-16px";

  return (
    <div
      className="group"
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible
          ? "translateX(0) scale(1)"
          : `translateX(${slideX}) scale(0.95)`,
        transition: isVisible
          ? "opacity 350ms var(--ease-spring), transform 400ms var(--ease-bounce)"
          : "opacity 200ms ease-out, transform 200ms ease-out",
        pointerEvents: "auto",
        minWidth: "280px",
        maxWidth: "380px",
      }}
    >
      <div
        className={`fintheon-toast-surface ${
          toast.variant === "watch" ? "fintheon-toast-watch" : ""
        }`}
      >
        <div
          className="flex items-start justify-between"
          style={{ padding: "10px 12px" }}
        >
          <div className="flex items-start" style={{ gap: "8px" }}>
            {cfg.label && (
              <span className="fintheon-toast-badge inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold tracking-widest flex-shrink-0 mt-0.5">
                {cfg.label}
              </span>
            )}
            {!cfg.label &&
              (toast.leadingGlyph ? (
                <span
                  aria-hidden="true"
                  className="flex-shrink-0 mt-0.5 text-[14px] leading-none"
                  style={{ color: "var(--fintheon-accent)" }}
                >
                  {toast.leadingGlyph}
                </span>
              ) : (
                <cfg.Icon
                  size={13}
                  className={`flex-shrink-0 mt-0.5 ${toast.variant === "updating" ? "animate-spin" : ""}`}
                  style={{ color: "var(--fintheon-accent)", opacity: 0.7 }}
                />
              ))}
            <div className="flex flex-col" style={{ gap: "2px" }}>
              <span
                className="text-[12px] font-medium leading-tight"
                style={{ color: "var(--fintheon-text)" }}
              >
                {toast.message}
              </span>
              {toast.description && (
                <span
                  className="text-[10px] leading-tight"
                  style={{ color: "var(--fintheon-muted)" }}
                >
                  {toast.description}
                </span>
              )}
              {toast.cta && (
                <div className="mt-1 flex items-center gap-1.5">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toast.cta!.onClick();
                      onDismiss(toast.id);
                    }}
                    className="rounded px-2 py-0.5 text-[10px] font-bold tracking-wide transition-colors"
                    style={{
                      color: "var(--fintheon-bg)",
                      backgroundColor: "var(--fintheon-accent)",
                    }}
                  >
                    {toast.cta.label}
                  </button>
                  {toast.secondaryCta && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toast.secondaryCta!.onClick();
                        onDismiss(toast.id);
                      }}
                      className="rounded px-2 py-0.5 text-[10px] font-bold tracking-wide transition-colors border"
                      style={{
                        color: "var(--fintheon-accent)",
                        borderColor: "var(--fintheon-accent)",
                        backgroundColor: "transparent",
                      }}
                    >
                      {toast.secondaryCta.label}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
          <div
            className="flex items-center flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
            style={{ gap: "2px", marginLeft: "8px" }}
          >
            {hasDND && (
              <button
                onClick={() => onBlock(toast)}
                title="Don't show again"
                className="flex items-center justify-center rounded transition-colors hover:bg-[var(--fintheon-accent)]/10"
                style={{
                  width: "20px",
                  height: "20px",
                  color: "var(--fintheon-muted)",
                }}
              >
                <BellOff size={11} />
              </button>
            )}
            <button
              onClick={() => onDismiss(toast.id)}
              className="flex items-center justify-center rounded transition-colors hover:bg-[var(--fintheon-accent)]/10"
              style={{
                width: "20px",
                height: "20px",
                color: "var(--fintheon-muted)",
              }}
            >
              <X size={11} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Container — split by position; Zen reroutes market toasts to bottom-left */
/* ------------------------------------------------------------------ */

export function ToastContainer() {
  const { toasts, dismissToast, blockNotificationType } = useToast();
  const [zenModeActive, setZenModeActive] = useState(() => {
    if (typeof document === "undefined") return false;
    return document.body.dataset.fintheonZenMode === "true";
  });

  useEffect(() => {
    const handleZenModeChange = (event: Event) => {
      const detail = (event as CustomEvent<{ active?: boolean }>).detail;
      setZenModeActive(Boolean(detail?.active));
    };

    window.addEventListener("fintheon:zen-mode-change", handleZenModeChange);
    setZenModeActive(document.body.dataset.fintheonZenMode === "true");

    return () => {
      window.removeEventListener(
        "fintheon:zen-mode-change",
        handleZenModeChange,
      );
    };
  }, []);

  if (toasts.length === 0) return null;

  const handleBlock = (toast: Toast) => {
    if (toast.notificationType) {
      blockNotificationType(toast.notificationType);
    }
    dismissToast(toast.id);
  };

  const bottomRight = toasts.filter(
    (t) => t.position === "bottom-right" && !zenModeActive,
  );
  const bottomLeft = toasts.filter(
    (t) =>
      (t.position !== "top-right" && t.position !== "bottom-right") ||
      zenModeActive,
  );
  const topRight = zenModeActive
    ? []
    : toasts.filter((t) => t.position === "top-right");

  return (
    <>
      {/* System toasts — bottom-left */}
      {bottomLeft.length > 0 && (
        <div
          className="fixed z-[100] flex flex-col items-start"
          style={{
            bottom: "24px",
            left: "24px",
            gap: "10px",
            pointerEvents: "none",
          }}
        >
          {bottomLeft.map((toast) => (
            <ToastItem
              key={toast.id}
              toast={toast}
              onDismiss={dismissToast}
              onBlock={handleBlock}
            />
          ))}
        </div>
      )}
      {/* Watch/RiskFlow toasts — bottom-right outside Zen */}
      {bottomRight.length > 0 && (
        <div
          className="fixed z-[100] flex flex-col items-end"
          style={{
            bottom: "38px",
            right: "24px",
            gap: "10px",
            pointerEvents: "none",
          }}
        >
          {bottomRight.map((toast) => (
            <ToastItem
              key={toast.id}
              toast={toast}
              onDismiss={dismissToast}
              onBlock={handleBlock}
            />
          ))}
        </div>
      )}
      {/* Trading/market toasts — top-right outside Zen */}
      {topRight.length > 0 && (
        <div
          className="fixed z-[100] flex flex-col items-end"
          style={{
            top: "70px",
            right: "24px",
            gap: "10px",
            pointerEvents: "none",
          }}
        >
          {topRight.map((toast) => (
            <ToastItem
              key={toast.id}
              toast={toast}
              onDismiss={dismissToast}
              onBlock={handleBlock}
            />
          ))}
        </div>
      )}
    </>
  );
}
